import { AuthFetchWithResponse } from 'src/Types/api'
import { Payload } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import {
  NotificationRequestModel,
  NotificationModel,
  NotificationOverviewModel,
  NotificationsStatsModel,
  NotificationImageUploadResponse,
} from '../models/notifications.model'
import { ApiResult } from 'src/Libs/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { NotificationDto } from '../dto/notifications.dto'
import fromDtoToModelTransformer from '../transformers/from-dto-to-model.transformers'
import { handleFetchError } from 'src/Libs/api/utils'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { isFlowTheme } from 'src/Libs/utils'

/*
 * Get all notifications for the current Platform (moblie or desktop)
 * fyi there are notifications that are show on desktop and mobile
 */
export const getNotifications = async (
  payload: Payload,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<NotificationModel[], AuthFetchError | Error>> => {
  if (isFlowTheme) return new ApiResult<NotificationModel[], AuthFetchError | Error>([], false) //TODO remove when Flow supports this feature
  try {
    const response = await authFetch<NotificationDto>({
      path: 'notifications/messages',
      payload,
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<NotificationModel[], AuthFetchError | Error>([], false)
    }
    if (response.status === StatusCodes.OK) {
      const model = fromDtoToModelTransformer(response.data as NotificationDto)
      return new ApiResult<NotificationModel[], AuthFetchError | Error>(model, false)
    }

    return new ApiResult<NotificationModel[], AuthFetchError | Error>([], true)
  } catch (error) {
    return handleFetchError<NotificationModel[]>(error)
  }
}

export const getNotificationsStats = async (
  payload: Payload,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<NotificationsStatsModel, AuthFetchError | Error>> => {
  if (isFlowTheme) return new ApiResult<NotificationsStatsModel, AuthFetchError | Error>(null, false) //TODO remove when Flow supports this feature
  try {
    const response = await authFetch<NotificationsStatsModel>({
      path: 'notifications/messages/stats',
      payload,
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<NotificationsStatsModel, AuthFetchError | Error>(response.data)
    }

    return new ApiResult<NotificationsStatsModel, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<NotificationsStatsModel>(error)
  }
}

/*
 * Sets readAt for a notifications to mark it as read.
 */
export const setNotificationReadAt = async (
  notificationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const response = await authFetch({
      path: `notifications/messages/${notificationId}/read`,
      method: 'POST',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
    })

    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<null, AuthFetchError | Error>(null, false)
    }

    return new ApiResult<null, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const deleteNotification = async (notificationId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  try {
    const response = await authFetch<void>({
      path: `notifications/messages/${notificationId}`,
      method: 'DELETE',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<void>(null, false)
    }

    return new ApiResult<void>(null, true)
  } catch (error) {
    return handleFetchError<void>(error)
  }
}

export const createNotification = async (notification: NotificationRequestModel, authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  try {
    const response = await authFetch<void>({
      path: `notifications/messages`,
      method: 'POST',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
      payload: notification,
    })

    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<number>(response.data as unknown as number, false)
    }

    return new ApiResult<number>(null, true)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

export const getNotificationById = async (
  notificationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<NotificationOverviewModel>> => {
  try {
    const response = await authFetch<NotificationOverviewModel>({
      path: `notifications/messages/${notificationId}`,
      method: 'GET',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<NotificationOverviewModel>(response.data)
    }

    return new ApiResult<NotificationOverviewModel>(null, true)
  } catch (error) {
    return handleFetchError<NotificationOverviewModel>(error)
  }
}

export const updateNotification = async (
  notificationId: number,
  payload: Partial<NotificationRequestModel>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null>> => {
  try {
    const response = await authFetch<void>({
      path: `notifications/messages/${notificationId}`,
      method: 'PUT',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
      payload,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<null>(null, false)
    }

    return new ApiResult<null>(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const uploadImageForNotification = async (
  formData: FormData,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<NotificationImageUploadResponse>> => {
  try {
    const response = await authFetch<NotificationImageUploadResponse>({
      path: `notifications/files`,
      method: 'POST',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
      payload: formData,
    })

    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<NotificationImageUploadResponse>(response.data, false)
    }

    return new ApiResult<NotificationImageUploadResponse>(null, true)
  } catch (error) {
    return handleFetchError<NotificationImageUploadResponse>(error)
  }
}

/**
 * Marks all notifications as read for current user.
 */
export const setAllNotificationsAsRead = async (
  userId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const response = await authFetch({
      path: 'notifications/messages/readAll',
      method: 'POST',
      endpoint: process.env.REACT_APP_NOTIFICATION_ENDPOINT,
      payload: { userId },
    })

    if (response.status === StatusCodes.CREATED || response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<null, AuthFetchError | Error>(null, false)
    }

    return new ApiResult<null, AuthFetchError | Error>(null, true)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}
